import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { UseFormSetValue } from 'react-hook-form'
import { Select, Switch } from 'utoniq-ui'

import { WallpaperPrintType } from '@/clients/utoniq-core/schema'
import { CreateItemProps } from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type PrintInputCellProps = {
  setValue: UseFormSetValue<CreateItemProps>
  printType?: WallpaperPrintType
  printCount?: number
  isPrintEnabled: boolean
}
export const PrintInputCell: Cell<PrintInputCellProps> = ({
  isPrintEnabled,
  printType,
  setValue,
  printCount,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const options = [
    { value: WallpaperPrintType.PhotoL, label: t(WallpaperPrintType.PhotoL) },
    {
      value: WallpaperPrintType.Photo_2l,
      label: t(WallpaperPrintType.Photo_2l),
    },
    {
      value: WallpaperPrintType.PhotoSquare,
      label: t(WallpaperPrintType.PhotoSquare),
    },
    { value: WallpaperPrintType.SealL, label: t(WallpaperPrintType.SealL) },
    { value: WallpaperPrintType.Seal_2l, label: t(WallpaperPrintType.Seal_2l) },
    {
      value: WallpaperPrintType.SealSquare,
      label: t(WallpaperPrintType.SealSquare),
    },
    {
      value: WallpaperPrintType.Postcard,
      label: t(WallpaperPrintType.Postcard),
    },
    {
      value: WallpaperPrintType.GlossyPaperA4,
      label: t(WallpaperPrintType.GlossyPaperA4),
    },
  ]
  return (
    <CellFrame sx={{ pb: 7 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">{t('printWallpaperLabel')}</Typography>
        <Switch
          checked={isPrintEnabled}
          sx={{
            '&& .Mui-disabled + .MuiSwitch-track': { bgcolor: 'grey.100' },
            '&& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
              bgcolor: 'primary.main',
            },
          }}
          onChange={() => {
            if (isPrintEnabled) {
              setValue('metadata.wallpaper.isPrintEnabled', false as never, {
                shouldValidate: true,
                shouldDirty: true,
              })
              setValue('metadata.wallpaper.printCount', null as never, {
                shouldValidate: true,
                shouldDirty: true,
              })
              setValue('metadata.wallpaper.printType', null as never, {
                shouldValidate: true,
                shouldDirty: true,
              })
              return
            }
            setValue('metadata.wallpaper.isPrintEnabled', true as never, {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('metadata.wallpaper.printCount', 1 as never, {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue(
              'metadata.wallpaper.printType',
              WallpaperPrintType.PhotoL as never,
              { shouldValidate: true, shouldDirty: true }
            )
          }}
        />
      </Stack>
      <Stack sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('printWallpaperDesc')}
        </Typography>
      </Stack>
      {isPrintEnabled && (
        <Stack pt={4}>
          <Typography variant="subtitle1">
            {t('printTypeLabel', '印刷タイプ')}
          </Typography>
          <RadioGroup
            value={printType ?? ''}
            onChange={(_, value) =>
              setValue('metadata.wallpaper.printType', value as never, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          >
            {options.map(({ label, value }) => (
              <FormControlLabel
                value={value}
                key={value}
                control={<Radio />}
                label={label}
              />
            ))}
          </RadioGroup>
        </Stack>
      )}
      {isPrintEnabled && (
        <Stack pt={4} spacing={2}>
          <Typography variant="subtitle1">
            {t('printCountLabel', '印刷枚数')}
          </Typography>
          <Select
            listItems={Array.from(Array(1).keys()).map(key => ({
              value: (key + 1).toString(),
              label: t('sheet', { number: key + 1 }),
            }))}
            value={(printCount ?? 1).toString()}
            onChange={value =>
              setValue('metadata.wallpaper.printCount', +value as never, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            formControlProps={{
              sx: { width: '100%', bgcolor: 'grey.50', minHeight: 'auto' },
            }}
          />
        </Stack>
      )}
    </CellFrame>
  )
}
